/**
 * Topics are now admin-managed via the backend `/topic` endpoint.
 * The hardcoded enum that used to live here has been replaced by a
 * collection + seeded defaults. Consumers fetch the live list via
 * `fetchTopics()` (server components only — no client-side hit needed
 * because the article payload already carries `topicLabel`).
 */

export interface Topic {
  _id: string;
  name: string;
  label: string;
  order?: number;
}

const API_BASE =
  process.env.API_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  process.env.NEXT_PUBLIC_BACKEND_URL ||
  "";

/**
 * Server-side fetch for the topic list. Used by the /blog index to
 * render pill filters. Failures are non-fatal — return empty array so
 * the page still renders.
 *
 * Cached in Next's Data Cache (topics change rarely). The /blog page's
 * on-demand revalidatePath("/blog") re-runs this fetch on admin saves,
 * and the 1-hour window catches the rare standalone topic edit — so we
 * get fresh data without a backend round trip on every visit.
 */
export async function fetchTopics(): Promise<Topic[]> {
  if (!API_BASE) return [];
  try {
    const res = await fetch(`${API_BASE}/topic`, { next: { revalidate: 3600 } });
    if (!res.ok) return [];
    return (await res.json()) as Topic[];
  } catch {
    return [];
  }
}

/** Slug → label lookup with safe fallback. */
export function topicLabel(slug: string | undefined, topics: Topic[]): string {
  if (!slug) return "";
  return topics.find((t) => t.name === slug)?.label ?? slug;
}

/**
 * Resolve a cover-image reference to a usable URL. Three accepted forms:
 *
 *   - Full URL (http/https): returned as-is.
 *   - Domain-relative path (`/blog/.../hero.jpg`): returned as-is — the
 *     browser resolves against the current origin, so files served from
 *     Next.js's `/public` work without configuration.
 *   - Bucket key (`blog/.../hero.jpg`): prepended with NEXT_PUBLIC_IMAGE_URL
 *     so prod articles continue to resolve against the S3 bucket.
 *
 * Missing values fall back to the generic property placeholder.
 */
export function articleImageUrl(coverImage?: string): string {
  if (!coverImage) return "/property-img.svg";
  if (/^https?:\/\//i.test(coverImage)) return coverImage;
  if (coverImage.startsWith("/")) return coverImage;
  const base = (process.env.NEXT_PUBLIC_IMAGE_URL || "").replace(/\/$/, "");
  if (!base) return coverImage;
  return `${base}/${coverImage}`;
}
